import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';

const SUBJECT_COLORS = ['#2F6BFF', '#8B5CF6', '#FF4DA6', '#FF7A2E', '#14B8A6', '#19B36B', '#FFD21F', '#14143A'];
const STATE_LABEL = { m: 'Mastered', e: 'Learning', r: 'Ready now', w: 'Waiting' };
// Admin lesson explorer: what each dot says about lessons (see LessonAdminSearch::STATUS_CODES).
const ADMIN_LABEL = { m: 'Has a published lesson', e: 'Lesson generating', f: 'Lesson failed', w: 'No lesson yet' };
const ADMIN_COLOR = { m: '#19B36B', e: '#2F6BFF', f: '#E5484D', w: '#54547D' };
const MID_Y = 95; // vertical centre of the funnel, in graph units
const ageLabel = (a) => (a === 11 ? '11+' : String(a));

// One shared fetch per page, however many visuals are on it.
let graphPromise = null;
function loadGraph(url) {
  if (!graphPromise) {
    graphPromise = fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then((r) => { if (!r.ok) throw new Error('graph'); return r.json(); })
      .catch((e) => { graphPromise = null; throw e; });
  }
  return graphPromise;
}

function useGraph(url) {
  const [state, setState] = useState({ status: 'loading', graph: null });
  useEffect(() => {
    let live = true;
    loadGraph(url).then((graph) => live && setState({ status: 'ready', graph })).catch(() => live && setState({ status: 'error', graph: null }));
    return () => { live = false; };
  }, [url]);
  return state;
}

function useReducedMotion() {
  const [reduced, setReduced] = useState(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const on = () => setReduced(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return reduced;
}

function Universe({ graph, variant, age, selected, onSelect, autoRotate, onInteract, focusSubject }) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const view = useRef({ yaw: 0.6, tilt: 0.38, w: 0, h: 0, dpr: 1 });
  const drag = useRef(null);
  const proj = useRef({ sx: null, sy: null, sc: null });
  const live = useRef({});
  const states = graph.states || null;

  const { nodes, edges } = graph;
  const prereqs = useMemo(() => {
    const map = Array.from({ length: nodes.length }, () => []);
    edges.forEach(([a, b]) => map[b].push(a));
    return map;
  }, [graph]);
  const chain = useMemo(() => {
    if (selected == null) return null;
    const seen = new Set([selected]);
    const stack = [selected];
    while (stack.length) { prereqs[stack.pop()].forEach((p) => { if (!seen.has(p)) { seen.add(p); stack.push(p); } }); }
    return seen;
  }, [selected, prereqs]);

  live.current = { age, selected, chain, autoRotate, variant, focusSubject };

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;
    const n = nodes.length;
    proj.current = { sx: new Float32Array(n), sy: new Float32Array(n), sc: new Float32Array(n) };
    const depth = new Float32Array(n);
    const order = Array.from({ length: n }, (_, i) => i);
    let raf = 0;
    let visible = true;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = wrap.clientWidth;
      const h = wrap.clientHeight;
      view.current.w = w; view.current.h = h; view.current.dpr = dpr;
      canvas.width = Math.round(w * dpr); canvas.height = Math.round(h * dpr);
    };
    const ro = new ResizeObserver(resize);
    ro.observe(wrap);
    resize();
    const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; }, { threshold: 0.01 });
    io.observe(wrap);

    const frame = () => {
      raf = requestAnimationFrame(frame);
      if (!visible || document.hidden) return;
      const L = live.current;
      const v = view.current;
      if (L.autoRotate && !drag.current) v.yaw += 0.0026;
      const { sx, sy, sc } = proj.current;
      const cosY = Math.cos(v.yaw), sinY = Math.sin(v.yaw), cosT = Math.cos(v.tilt), sinT = Math.sin(v.tilt);
      const k = Math.min(v.w / 330, v.h / 270);
      const k_ = k;
      const cx = v.w / 2, cy = v.h / 2;
      for (let i = 0; i < n; i += 1) {
        const [x, y, z] = nodes[i];
        const x1 = x * cosY - z * sinY;
        const z1 = x * sinY + z * cosY;
        const y1 = y - MID_Y;
        const y2 = y1 * cosT - z1 * sinT;
        const z2 = y1 * sinT + z1 * cosT;
        const s = 520 / (520 + z2 + 330) * 2.0;
        sx[i] = cx + x1 * s * k;
        sy[i] = cy - y2 * s * k;
        sc[i] = s;
        depth[i] = z2;
      }
      order.sort((a, b) => depth[b] - depth[a]);

      const dpr = v.dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, v.w, v.h);

      const explorer = L.variant !== 'hero';
      const lit = (i) => (states ? states[i] !== 'x' : !explorer || nodes[i][4] <= L.age);

      // age levels: faint rings so the funnel reads as stacked ages
      const ringAlphaBase = explorer ? 0.16 : 0.08;
      graph.ages.forEach((a) => {
        const R = 30 + 13.5 * (a - 4), yy = (a - 4) * 26;
        const current = explorer && !states && a === L.age;
        ctx.beginPath();
        for (let k = 0; k <= 56; k += 1) {
          const th = (k / 56) * 6.2832;
          const x = R * Math.cos(th), z = R * Math.sin(th);
          const x1 = x * cosY - z * sinY, z1 = x * sinY + z * cosY, y1 = yy - MID_Y;
          const y2 = y1 * cosT - z1 * sinT, z2 = y1 * sinT + z1 * cosT;
          const sc2 = 520 / (520 + z2 + 330) * 2.0;
          const px = cx + x1 * sc2 * k_, py = cy - y2 * sc2 * k_;
          if (k === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
        }
        ctx.lineWidth = current ? 1.8 : 1;
        ctx.strokeStyle = current ? 'rgba(47,107,255,0.75)' : `rgba(20,20,58,${ringAlphaBase})`;
        ctx.stroke();
        if (explorer && !states) {
          // label at the ring's right-hand point
          const x1 = R * cosY, z1 = R * sinY, y1 = yy - MID_Y;
          const y2 = y1 * cosT - z1 * sinT, z2 = y1 * sinT + z1 * cosT;
          const sc2 = 520 / (520 + z2 + 330) * 2.0;
          ctx.font = current ? '700 12px "Atkinson Hyperlegible Next", sans-serif' : '600 11px "Atkinson Hyperlegible Next", sans-serif';
          ctx.fillStyle = current ? '#2F6BFF' : 'rgba(84,84,125,0.8)';
          ctx.fillText(ageLabel(a), cx + x1 * sc2 * k_ + 8, cy - y2 * sc2 * k_ + 4);
        }
      });

      // links
      ctx.lineWidth = 1;
      ctx.strokeStyle = 'rgba(20,20,58,0.15)';
      ctx.beginPath();
      for (let e = 0; e < edges.length; e += 1) {
        const [a, b] = edges[e];
        if (!lit(b) || !lit(a)) continue;
        if (L.chain && L.chain.has(a) && L.chain.has(b)) continue;
        ctx.moveTo(sx[a], sy[a]); ctx.lineTo(sx[b], sy[b]);
      }
      ctx.stroke();
      if (L.chain) {
        ctx.lineWidth = 1.6;
        ctx.strokeStyle = 'rgba(47,107,255,0.85)';
        ctx.beginPath();
        for (let e = 0; e < edges.length; e += 1) {
          const [a, b] = edges[e];
          if (L.chain.has(a) && L.chain.has(b)) { ctx.moveTo(sx[a], sy[a]); ctx.lineTo(sx[b], sy[b]); }
        }
        ctx.stroke();
      }

      // skills, far to near
      for (let o = 0; o < n; o += 1) {
        const i = order[o];
        const near = Math.max(0.45, Math.min(1, 1.15 - depth[i] / 320));
        const inChain = L.chain ? L.chain.has(i) : true;
        let r = (L.variant === 'hero' ? 2.1 : 2.4) * sc[i] * (k > 1 ? Math.min(k, 1.6) : 1);
        let color;
        let alpha = near;
        if (states) {
          const st = states[i];
          color = st === 'm' ? '#19B36B' : st === 'e' ? '#2F6BFF' : st === 'r' ? '#FFD21F' : st === 'f' ? '#E5484D' : '#54547D';
          alpha = st === 'x' ? 0.06 * near : st === 'w' ? 0.35 * near : near;
          if (L.variant === 'admin' && (st === 'm' || st === 'e' || st === 'f')) r *= 1.5;
        } else if (!lit(i)) {
          color = '#54547D'; alpha = 0.16 * near;
        } else {
          color = SUBJECT_COLORS[nodes[i][3] % SUBJECT_COLORS.length];
          if (explorer && nodes[i][4] === L.age) r *= 1.35;
        }
        if (L.chain && !inChain) alpha *= 0.25;
        if (L.focusSubject != null && nodes[i][3] !== L.focusSubject) alpha *= 0.15;
        ctx.globalAlpha = alpha;
        ctx.fillStyle = color;
        ctx.beginPath(); ctx.arc(sx[i], sy[i], r, 0, 6.2832); ctx.fill();
        if (states && states[i] === 'r') { ctx.globalAlpha = alpha * 0.6; ctx.strokeStyle = '#2F6BFF'; ctx.lineWidth = 1; ctx.beginPath(); ctx.arc(sx[i], sy[i], r + 2.5, 0, 6.2832); ctx.stroke(); }
      }
      ctx.globalAlpha = 1;
      if (L.selected != null) {
        const i = L.selected;
        ctx.strokeStyle = '#14143A'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.arc(sx[i], sy[i], 9, 0, 6.2832); ctx.stroke();
        ctx.fillStyle = '#14143A'; ctx.beginPath(); ctx.arc(sx[i], sy[i], 3.2, 0, 6.2832); ctx.fill();
      }
    };
    raf = requestAnimationFrame(frame);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); io.disconnect(); };
  }, [graph]);

  const pick = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const px = e.clientX - rect.left, py = e.clientY - rect.top;
    const { sx, sy } = proj.current;
    let best = -1, bestD = 14 * 14;
    for (let i = 0; i < nodes.length; i += 1) {
      if (live.current.variant !== 'hero' && !states && nodes[i][4] > live.current.age) continue;
      if (states && states[i] === 'x') continue;
      const d = (sx[i] - px) ** 2 + (sy[i] - py) ** 2;
      if (d < bestD) { bestD = d; best = i; }
    }
    return best;
  };

  const down = (e) => { canvasRef.current.setPointerCapture?.(e.pointerId); drag.current = { x: e.clientX, y: e.clientY, moved: false }; };
  const move = (e) => {
    const d = drag.current; if (!d) return;
    const dx = e.clientX - d.x, dy = e.clientY - d.y;
    if (!d.moved && Math.hypot(dx, dy) < 5) return;
    if (!d.moved) { d.moved = true; onInteract(); }
    view.current.yaw += dx * 0.008;
    view.current.tilt = Math.min(0.85, Math.max(0.02, view.current.tilt + dy * 0.004));
    d.x = e.clientX; d.y = e.clientY;
  };
  const up = (e) => {
    const d = drag.current; drag.current = null;
    if (d && !d.moved) { onInteract(); const i = pick(e); onSelect(i >= 0 ? i : null); }
  };
  const keys = (e) => {
    const step = { ArrowLeft: [-0.12, 0], ArrowRight: [0.12, 0], ArrowUp: [0, -0.06], ArrowDown: [0, 0.06] }[e.key];
    if (!step) return;
    e.preventDefault(); onInteract();
    view.current.yaw += step[0];
    view.current.tilt = Math.min(0.85, Math.max(0.02, view.current.tilt + step[1]));
  };

  return (
    <div ref={wrapRef} className={`relative w-full aspect-square`}>
      <canvas ref={canvasRef} className="absolute inset-0 h-full w-full cursor-grab touch-pan-y active:cursor-grabbing focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue rounded-md"
        style={{ touchAction: 'pan-y' }} tabIndex={0} role="img"
        aria-label={`A 3D map of ${nodes.length.toLocaleString()} skills. Height is age, each dot is a skill and each line says one skill must come first. Drag or use the arrow keys to turn it.`}
        onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={() => { drag.current = null; }} onKeyDown={keys} />
    </div>
  );
}

export default function SkillUniverse({ url, variant = 'explorer', ctaLabel, ctaPath, mapPath, overlay, lessonCounts, topicUrl }) {
  const { status, graph: baseGraph } = useGraph(url);
  // Admin lesson explorer: replace any per-child states with the filter overlay computed server-side.
  const graph = useMemo(() => {
    if (!baseGraph || variant !== 'admin' || !overlay) return baseGraph;
    return { ...baseGraph, states: baseGraph.keys.map((k) => overlay.codes[k] || overlay.default) };
  }, [baseGraph, overlay, variant]);
  const reduced = useReducedMotion();
  const [age, setAge] = useState(6);
  const [selected, setSelected] = useState(null);
  const [paused, setPaused] = useState(false);
  const [focusSubject, setFocusSubject] = useState(null);
  const interact = useCallback(() => setPaused(true), []);
  const autoRotate = !reduced && !paused;

  if (status === 'loading') return <div role="status" className="grid aspect-square place-items-center rounded-md border-2 border-ink bg-paper-deep text-base text-ink-soft">Drawing the skill map…</div>;
  if (status === 'error') return <p role="alert" className="rounded-md border-2 border-ink bg-paper-deep p-6 text-base text-ink-soft">We couldn't draw the skill map right now. Please refresh in a moment.</p>;

  const states = graph.states;
  const child = graph.child;
  const counts = states ? [...states].reduce((acc, c) => ({ ...acc, [c]: (acc[c] || 0) + 1 }), {}) : null;
  const upTo = graph.nodes.filter((nd) => nd[4] <= age).length;
  const hl = graph.highlights.find((h) => h.age === age);
  const sel = selected != null ? { name: graph.names[selected], subject: graph.subjects[graph.nodes[selected][3]], age: graph.nodes[selected][4], chain: null } : null;
  if (sel) {
    const seen = new Set([selected]); const stack = [selected];
    const pre = {}; graph.edges.forEach(([a, b]) => { (pre[b] ||= []).push(a); });
    while (stack.length) { (pre[stack.pop()] || []).forEach((p) => { if (!seen.has(p)) { seen.add(p); stack.push(p); } }); }
    sel.chain = seen.size - 1;
  }

  const universe = <Universe graph={graph} variant={variant} age={age} selected={selected} onSelect={setSelected} autoRotate={autoRotate} onInteract={interact} focusSubject={focusSubject} />;

  if (variant === 'admin') {
    const code = selected != null ? states[selected] : null;
    const key = selected != null ? graph.keys[selected] : null;
    const filterHref = key && topicUrl ? (() => { const u = new URL(topicUrl, window.location.origin); u.searchParams.set('topic_key', key); return u.pathname + u.search; })() : null;
    const shown = states.filter((c) => c !== 'x').length;
    const total = graph.nodes.length;
    const directPrereqs = selected != null ? graph.edges.filter(([, b]) => b === selected).map(([a2]) => graph.names[a2]).slice(0, 4) : [];
    return (
      <div className="space-y-3">
        <div className="rounded-md border-2 border-ink bg-white p-2">{universe}</div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-ink-soft">{shown.toLocaleString()} of {total.toLocaleString()} skills match. Tap a dot to trace it.</p>
          <button type="button" onClick={() => setPaused((p) => !p)} className="min-h-[44px] rounded-full border-2 border-ink px-4 text-sm font-bold text-ink hover:border-blue focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue">
            {paused || reduced ? 'Turn the map' : 'Pause'}
          </button>
        </div>
        <ul role="list" aria-label="Colours" className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-ink-soft">
          {Object.entries(ADMIN_LABEL).map(([c, l]) => (
            <li key={c} className="inline-flex items-center gap-2"><span aria-hidden="true" className="h-3 w-3 rounded-full" style={{ background: ADMIN_COLOR[c] }} />{l}</li>
          ))}
        </ul>
        {sel && (
          <div className="rounded-md border-2 border-blue bg-white p-4 space-y-1" aria-live="polite">
            <p className="text-lg font-extrabold text-ink">{sel.name}</p>
            <p className="text-base text-ink-soft">{sel.subject} · age {ageLabel(sel.age)} · {ADMIN_LABEL[code]}</p>
            <p className="text-base text-ink">{(lessonCounts && lessonCounts[key]) || 0} matching lesson{((lessonCounts && lessonCounts[key]) || 0) === 1 ? '' : 's'} · {sel.chain} earlier skills lead up to it</p>
            {directPrereqs.length > 0 && <p className="text-base text-ink-soft"><span className="font-bold text-ink">Builds on:</span> {directPrereqs.join(', ')}</p>}
            {filterHref && <a href={filterHref} className="mt-1 inline-flex min-h-[44px] items-center font-bold text-blue underline">Show only this skill's lessons</a>}
          </div>
        )}
      </div>
    );
  }

  if (variant === 'hero') {
    return (
      <figure className="relative">
        {universe}
        <figcaption className="mt-2 text-center text-sm text-ink-soft">
          Every dot is a real skill, from age 4 up. Drag to turn it.
          {sel && <span className="mt-1 block font-bold text-ink">{sel.name} <span className="font-normal text-ink-soft">· {sel.subject}, age {ageLabel(sel.age)} · {sel.chain} skills lead up to it</span></span>}
        </figcaption>
      </figure>
    );
  }

  const total = graph.nodes.length;
  const statChips = [
    [graph.stats?.skills ?? total, 'skills'],
    [graph.stats?.domains ?? '', 'topic areas'],
    [graph.stats?.links ?? graph.edges.length, 'prerequisite links'],
  ];
  const directPrereqs = selected != null ? graph.edges.filter(([, b]) => b === selected).map(([a2]) => graph.names[a2]).slice(0, 4) : [];

  // Per-subject numbers: what is new at this age (visitors) or how lit each subject is (a signed-in child).
  const subjectRows = states
    ? graph.subjects.map((name, i) => {
        const c = { m: 0, e: 0, r: 0, w: 0 };
        graph.nodes.forEach((nd, idx) => { if (nd[3] === i) c[states[idx]] += 1; });
        const t = c.m + c.e + c.r + c.w;
        return { name, index: i, count: t, parts: c, pct: t ? Math.round(((c.m + c.e * 0.5) / t) * 100) : 0 };
      })
    : (hl ? hl.by_subject.map((b) => ({ name: b.subject, index: graph.subjects.indexOf(b.subject), count: b.count })) : []);
  const maxCount = Math.max(1, ...subjectRows.map((r) => r.count));
  const cumPct = Math.round((upTo / total) * 100);

  return (
    <div className="grid gap-6 lg:grid-cols-12 lg:items-start">
      {/* The map: compact, on the left */}
      <div className="lg:col-span-5 space-y-3 lg:sticky lg:top-24">
        <div className="rounded-md border-2 border-ink bg-white p-2">{universe}</div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-ink-soft">Drag to turn it. Tap a dot to trace what leads up to it.</p>
          <button type="button" onClick={() => setPaused((p) => !p)} className="min-h-[44px] rounded-full border-2 border-ink px-4 text-sm font-bold text-ink hover:border-blue focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue">
            {paused || reduced ? 'Turn the map' : 'Pause'}
          </button>
        </div>
        <ul role="list" aria-label="Colours" className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-ink-soft">
          {!states && graph.subjects.map((nm, i) => (
            <li key={nm}>
              <button type="button" onMouseEnter={() => setFocusSubject(i)} onMouseLeave={() => setFocusSubject(null)} onFocus={() => setFocusSubject(i)} onBlur={() => setFocusSubject(null)}
                className="inline-flex min-h-[32px] items-center gap-2 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue">
                <span aria-hidden="true" className="h-3 w-3 rounded-full" style={{ background: SUBJECT_COLORS[i % SUBJECT_COLORS.length] }} />{nm}
              </button>
            </li>
          ))}
          {states && [['#19B36B', 'Mastered'], ['#2F6BFF', 'Learning'], ['#FFD21F', 'Ready now'], ['#54547D', 'Waiting']].map(([c, l]) => (
            <li key={l} className="inline-flex items-center gap-2"><span aria-hidden="true" className="h-3 w-3 rounded-full" style={{ background: c }} />{l}</li>
          ))}
        </ul>
      </div>

      {/* What the map says: on the right */}
      <div className="lg:col-span-7 space-y-5">
        <dl className="flex flex-wrap gap-x-8 gap-y-2">
          {statChips.map(([n, label]) => (
            <div key={label}><dd className="text-2xl font-extrabold text-ink">{typeof n === 'number' ? n.toLocaleString() : n}</dd><dt className="text-sm text-ink-soft">{label}</dt></div>
          ))}
        </dl>

        {states ? (
          <div className="space-y-1">
            <p className="text-base text-ink-soft">{child ? `${child.name}'s map` : 'Your child'}</p>
            <p className="text-3xl font-extrabold text-ink"><span className="text-green">{counts.m || 0}</span> mastered <span className="text-ink-soft">·</span> <span className="text-blue">{counts.r || 0}</span> ready now</p>
            <p className="text-base text-ink-soft">{counts.e || 0} in progress, {counts.w || 0} waiting on earlier skills.</p>
          </div>
        ) : (
          <div className="space-y-3">
            <div role="radiogroup" aria-label="Pick an age" className="flex flex-wrap gap-2">
              {graph.ages.map((a) => (
                <button key={a} type="button" role="radio" aria-checked={age === a} onClick={() => setAge(a)}
                  className={`min-h-[44px] min-w-[44px] rounded-full border px-4 text-base font-extrabold focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue ${age === a ? 'border-blue bg-blue/15 text-blue' : 'border-rule text-ink hover:border-blue'}`}>
                  {ageLabel(a)}
                </button>
              ))}
            </div>
            {hl && (
              <div aria-live="polite" className="space-y-2">
                <p className="text-lg text-ink">At age {ageLabel(age)}, children typically meet</p>
                <p className="text-5xl font-extrabold leading-none text-blue">{hl.new_skills}<span className="ml-2 text-xl font-bold text-ink">new skills</span></p>
                <div className="pt-1">
                  <div className="flex items-center justify-between text-sm font-bold text-ink-soft"><span>On the map up to age {ageLabel(age)}</span><span>{upTo.toLocaleString()} of {total.toLocaleString()} · {cumPct}%</span></div>
                  <div className="mt-1.5 h-3 overflow-hidden rounded-full bg-paper-shade"><div className="h-full rounded-full bg-green-fill transition-[width] duration-500 motion-reduce:transition-none" style={{ width: `${cumPct}%` }} /></div>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="space-y-2">
          <p className="text-sm font-bold text-ink-soft">{states ? 'Progress by subject' : `Where age ${ageLabel(age)} skills come from`}</p>
          <ul role="list" className="space-y-2">
            {subjectRows.map((r) => (
              <li key={r.name}>
                <button type="button" onMouseEnter={() => setFocusSubject(r.index)} onMouseLeave={() => setFocusSubject(null)} onFocus={() => setFocusSubject(r.index)} onBlur={() => setFocusSubject(null)}
                  className="block w-full rounded-lg px-1 py-1 text-left hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue">
                  <span className="flex items-center justify-between text-base">
                    <span className="inline-flex items-center gap-2 font-bold text-ink"><span aria-hidden="true" className="h-3 w-3 rounded-full" style={{ background: SUBJECT_COLORS[r.index % SUBJECT_COLORS.length] }} />{r.name}</span>
                    <span className="font-extrabold text-ink">{states ? `${r.pct}% lit` : r.count}</span>
                  </span>
                  <span className="mt-1 block h-2 overflow-hidden rounded-full bg-paper-shade" aria-hidden="true">
                    {states ? (
                      <span className="flex h-full">
                        <span style={{ width: `${(r.parts.m / (r.count || 1)) * 100}%`, background: '#19B36B' }} />
                        <span style={{ width: `${(r.parts.e / (r.count || 1)) * 100}%`, background: '#2F6BFF' }} />
                        <span style={{ width: `${(r.parts.r / (r.count || 1)) * 100}%`, background: '#FFD21F', opacity: 0.45 }} />
                      </span>
                    ) : (
                      <span className="block h-full rounded-full transition-[width] duration-500 motion-reduce:transition-none" style={{ width: `${(r.count / maxCount) * 100}%`, background: SUBJECT_COLORS[r.index % SUBJECT_COLORS.length] }} />
                    )}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>

        {!states && hl && hl.examples.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-bold text-ink-soft">A few skills at age {ageLabel(age)}</p>
            <ul role="list" className="grid gap-2 sm:grid-cols-2">
              {hl.examples.map((ex) => {
                const si = graph.subjects.indexOf(ex.subject);
                return (
                  <li key={ex.name} className="flex items-start gap-2 rounded-md border-2 border-ink bg-white px-3 py-2 text-base text-ink">
                    <span aria-hidden="true" className="mt-2 h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: SUBJECT_COLORS[si % SUBJECT_COLORS.length] }} />
                    <span><span className="font-bold">{ex.name}</span> <span className="text-sm text-ink-soft">· {ex.subject}</span></span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {sel && (
          <div className="rounded-md border-2 border-blue bg-white p-4 space-y-1" aria-live="polite">
            <p className="text-lg font-extrabold text-ink">{sel.name}</p>
            <p className="text-base text-ink-soft">{sel.subject} · age {ageLabel(sel.age)}{states ? ` · ${STATE_LABEL[states[selected]]}` : ''}</p>
            <p className="text-base text-ink">{sel.chain > 0 ? `${sel.chain} earlier skills lead up to this one, traced in blue.` : 'A starting skill. Nothing has to come before it.'}</p>
            {directPrereqs.length > 0 && <p className="text-base text-ink-soft"><span className="font-bold text-ink">Builds on:</span> {directPrereqs.join(', ')}</p>}
          </div>
        )}

        {(ctaPath || mapPath) && (
          <div className="flex flex-wrap gap-3 pt-1">
            {ctaPath && <a href={ctaPath} className="inline-flex min-h-[48px] items-center justify-center rounded-md bg-ink px-6 text-base font-extrabold text-paper focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue">{ctaLabel || 'Start free'}</a>}
            {mapPath && <a href={mapPath} className="inline-flex min-h-[48px] items-center justify-center rounded-md border-2 border-blue px-6 text-base font-extrabold text-blue hover:bg-blue/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue">Open {child ? `${child.name}'s` : 'the'} quest map</a>}
          </div>
        )}
      </div>
    </div>
  );
}
